import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  AppBar, Toolbar, Typography, Button, Box, Paper, Grid,
  TextField, IconButton, Chip, Hidden, Fab, Tooltip
} from '@material-ui/core';
import { Add, Search, Refresh, Event as EventIcon, ArrowForward } from '@material-ui/icons';
import { useHistory } from 'react-router-dom';
import { AppDataContext } from '../../../common/AppContext';
import RouteConstants from '../../../common/RouteConstants';
import Loader from '../../../sharedComponents/Loader/Loader';
import AddNewEvent from '../../master/evaluationEvents/AddNewEvent';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
  },
  appbarBackground: {
    backgroundColor: '#fff',
    color: theme.palette.text.primary,
  },
  title: {
    flexGrow: 1,
  },
  content: {
    padding: theme.spacing(2),
    flexGrow: 1,
  },
  searchBar: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  eventCard: {
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(1.5),
    cursor: 'pointer',
    transition: 'box-shadow 0.2s, border-color 0.2s',
    '&:hover': {
      boxShadow: theme.shadows[4],
      borderColor: '#1d428a',
    },
  },
  eventHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  eventMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(8, 2),
    textAlign: 'center',
  },
}));

const EvaluationEventListPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const {
    evaluationEvents,
    getUpdatedEvaluationEvents,
    isLoadingEvents,
    addSnackMessages,
    gameTypeList,
  } = useContext(AppDataContext);

  const [searchTerm, setSearchTerm] = useState('');
  const [openAddEvent, setOpenAddEvent] = useState(false);

  useEffect(() => {
    getUpdatedEvaluationEvents();
  }, []);

  const filteredEvents = (evaluationEvents || []).filter((evt) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      (evt.eventName || '').toLowerCase().includes(q) ||
      (evt.gameTypeName || '').toLowerCase().includes(q) ||
      (evt.countryName || '').toLowerCase().includes(q) ||
      (evt.city || '').toLowerCase().includes(q)
    );
  });

  const handleEventClick = (event) => {
    history.push({
      pathname: RouteConstants.ADMIN_PROSPECT_EVALUATION_HOME,
      state: { evaluationEvent: event },
    });
  };

  const handleAddClick = () => {
    setOpenAddEvent(true);
  };

  const handleEventAdded = () => {
    getUpdatedEvaluationEvents();
  };

  const showAlert = (msg, severity) => {
    addSnackMessages([{ open: true, severity, message: msg }]);
  };

  return (
    <div className={classes.root}>
      <AppBar position="static" className={classes.appbarBackground}>
        <Toolbar style={{ justifyContent: 'space-between' }}>
          <Typography variant="h6" className={classes.title}>
            Evaluation Events
          </Typography>
          <Hidden xsDown>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<Add />}
              onClick={handleAddClick}
              style={{ textTransform: 'none' }}
            >
              Create Evaluation Event
            </Button>
          </Hidden>
        </Toolbar>
      </AppBar>

      <Box className={classes.content}>
        {/* Search */}
        <Box className={classes.searchBar}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search events by name, game type, location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: <Search fontSize="small" color="action" style={{ marginRight: 8 }} />,
            }}
          />
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => getUpdatedEvaluationEvents()}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Event List */}
        {filteredEvents.length === 0 && !isLoadingEvents ? (
          <Box className={classes.emptyState}>
            <EventIcon style={{ fontSize: 64, color: '#ccc', marginBottom: 16 }} />
            <Typography variant="h6" color="textSecondary" gutterBottom>
              {searchTerm ? 'No matching events found' : 'No Evaluation Events'}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ maxWidth: 400, marginBottom: 24 }}>
              {searchTerm
                ? 'Try adjusting your search criteria.'
                : 'Create an evaluation event to get started. Events group evaluations for a specific time period and location.'}
            </Typography>
            {!searchTerm && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={handleAddClick}
                style={{ textTransform: 'none' }}
              >
                Create First Event
              </Button>
            )}
          </Box>
        ) : (
          filteredEvents.map((evt) => (
            <Paper
              key={evt.id}
              variant="outlined"
              className={classes.eventCard}
              onClick={() => handleEventClick(evt)}
            >
              <Box className={classes.eventHeader}>
                <Box>
                  <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                    {evt.eventName}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gridGap={8}>
                  {evt.evaluationModeDisplayName && (
                    <Chip
                      size="small"
                      label={evt.evaluationModeDisplayName}
                      color="primary"
                      variant="outlined"
                      style={{ fontSize: '0.7rem', height: 24 }}
                    />
                  )}
                  <ArrowForward fontSize="small" color="action" />
                </Box>
              </Box>
              <Box className={classes.eventMeta}>
                {evt.eventFrom && (
                  <span className={classes.metaItem}>
                    <EventIcon style={{ fontSize: 16 }} />
                    {evt.eventFrom} {evt.eventTo ? `— ${evt.eventTo}` : ''}
                  </span>
                )}
                {evt.gameTypeName && (
                  <Chip size="small" label={evt.gameTypeName} variant="outlined" style={{ fontSize: '0.7rem', height: 22 }} />
                )}
                {evt.countryName && (
                  <span className={classes.metaItem}>{evt.city ? `${evt.city}, ` : ''}{evt.stateName || ''} {evt.countryName}</span>
                )}
              </Box>
            </Paper>
          ))
        )}
      </Box>

      <Hidden smUp>
        <Fab
          color="primary"
          aria-label="add"
          style={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={handleAddClick}
        >
          <Add />
        </Fab>
      </Hidden>

      {openAddEvent && (
        <AddNewEvent
          open={openAddEvent}
          handleCloseClick={() => setOpenAddEvent(false)}
          showAlert={showAlert}
          handleEventAdded={handleEventAdded}
          evaluationEvents={evaluationEvents}
          editEvent={{ id: 0, eventName: '', seasonCycleYear: '', seasonType: 0, seasonTypeName: '', isNBAEvent: true, eventFrom: null, eventTo: null, gameType: 0, country: 0, state: 0, stateOther: '', city: '', evaluationMode: '' }}
        />
      )}

      <Loader isLoading={isLoadingEvents} />
    </div>
  );
};

export default EvaluationEventListPage;
